(() => {
  'use strict';

  if (window.VexxQuickViewLoaded) return;
  window.VexxQuickViewLoaded = true;

  const ROOT_URL = window.Shopify?.routes?.root || '/';
  let lastTrigger = null;
  let closeTimer = null;

  const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const formatMoney = (cents) => {
    const amount = (Number(cents || 0) / 100).toFixed(2);
    const format = window.themeMoneyFormat || '${{amount}}';
    return format
      .replace('{{amount}}', amount)
      .replace('{{ amount }}', amount);
  };

  const variantOptions = (variant) => {
    if (Array.isArray(variant?.options)) {
      return variant.options.map((value) => String(value));
    }

    return [variant?.option1, variant?.option2, variant?.option3]
      .filter((value) => value !== null && value !== undefined)
      .map((value) => String(value));
  };

  const productOptions = (product) => {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const rawOptions = Array.isArray(product?.options) ? product.options : [];

    if (rawOptions.length && typeof rawOptions[0] === 'object') {
      return rawOptions.map((option, index) => ({
        name: option.name || `Option ${index + 1}`,
        position: Number(option.position) || index + 1,
        values: Array.isArray(option.values)
          ? option.values.map(String)
          : [...new Set(variants.map((variant) => variantOptions(variant)[index]).filter(Boolean))],
      }));
    }

    return rawOptions.map((name, index) => ({
      name: String(name || `Option ${index + 1}`),
      position: index + 1,
      values: [...new Set(variants.map((variant) => variantOptions(variant)[index]).filter(Boolean))],
    }));
  };


  const getCartToastRegion = () => {
    let region = document.getElementById('vexx-add-to-cart-toast-region');
    if (region) return region;

    region = document.createElement('div');
    region.id = 'vexx-add-to-cart-toast-region';
    region.className = 'vexx-cart-toast-region';
    region.setAttribute('aria-live', 'polite');
    region.setAttribute('aria-atomic', 'true');
    document.body.appendChild(region);
    return region;
  };

  const showCartToast = ({ type = 'success', title, message }) => {
    const region = getCartToastRegion();
    region.replaceChildren();

    const toast = document.createElement('div');
    toast.className = `vexx-cart-toast vexx-cart-toast--${type}`;
    toast.setAttribute('role', type === 'error' ? 'alert' : 'status');

    const icon = document.createElement('span');
    icon.className = 'vexx-cart-toast__icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = type === 'error' ? '!' : '✓';

    const copy = document.createElement('div');
    copy.className = 'vexx-cart-toast__copy';

    const heading = document.createElement('strong');
    heading.className = 'vexx-cart-toast__title';
    heading.textContent = title;

    const body = document.createElement('span');
    body.className = 'vexx-cart-toast__message';
    body.textContent = message;

    const close = document.createElement('button');
    close.className = 'vexx-cart-toast__close';
    close.type = 'button';
    close.setAttribute('aria-label', 'Dismiss notification');
    close.textContent = '×';

    copy.append(heading, body);
    toast.append(icon, copy, close);
    region.appendChild(toast);

    const dismiss = () => {
      toast.classList.remove('is-visible');
      window.setTimeout(() => toast.remove(), 220);
    };

    close.addEventListener('click', dismiss, { once: true });
    window.requestAnimationFrame(() => toast.classList.add('is-visible'));
    window.setTimeout(dismiss, type === 'error' ? 5200 : 3800);
  };

  const forceCartDrawerClosed = () => {
    const drawer = document.getElementById('cart-drawer');
    const overlay = document.getElementById('cart-drawer-overlay');

    drawer?.classList.remove('open');
    drawer?.setAttribute('aria-hidden', 'true');

    overlay?.classList.remove('open');
    overlay?.setAttribute('aria-hidden', 'true');

    document.querySelectorAll('[data-open-cart]').forEach((button) => {
      button.setAttribute('aria-expanded', 'false');
    });

    [
      'cart-drawer-is-open',
      'cart-is-open',
      'vexx-cart-is-open',
      'drawer-open'
    ].forEach((className) => {
      document.documentElement.classList.remove(className);
      document.body.classList.remove(className);
    });

    document.documentElement.style.removeProperty('overflow');
    document.body.style.removeProperty('overflow');
  };

  const updateCartBadges = (itemCount) => {
    document.querySelectorAll('[data-open-cart] .badge').forEach((badge) => {
      badge.textContent = String(itemCount);
      badge.hidden = itemCount <= 0;
      badge.classList.remove('vexx-cart-badge-bump');
      void badge.offsetWidth;
      badge.classList.add('vexx-cart-badge-bump');
      window.setTimeout(() => badge.classList.remove('vexx-cart-badge-bump'), 450);
    });
  };

  const refreshCartDrawerMarkup = async () => {
    const header = document.getElementById('site-header');
    const sectionWrapper = header?.closest('[id^="shopify-section-"]');
    const sectionId = sectionWrapper?.id?.replace(/^shopify-section-/, '');

    if (!sectionId) return;

    const sectionUrl = new URL(window.location.href);
    sectionUrl.searchParams.set('sections', sectionId);

    const response = await fetch(sectionUrl.toString(), {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) return;

    const sections = await response.json();
    const freshHeaderHtml = sections?.[sectionId];
    if (!freshHeaderHtml) return;

    const parsed = new DOMParser().parseFromString(freshHeaderHtml, 'text/html');
    const freshDrawer = parsed.getElementById('cart-drawer');
    const currentDrawer = document.getElementById('cart-drawer');

    if (!freshDrawer || !currentDrawer) return;

    const freshBody = freshDrawer.querySelector('.cart-drawer__body');
    const currentBody = currentDrawer.querySelector('.cart-drawer__body');

    if (freshBody && currentBody) {
      currentBody.replaceWith(freshBody.cloneNode(true));
    }

    const freshFoot = freshDrawer.querySelector('.cart-drawer__foot');
    const currentFoot = currentDrawer.querySelector('.cart-drawer__foot');

    if (freshFoot && currentFoot) {
      currentFoot.replaceWith(freshFoot.cloneNode(true));
    } else if (freshFoot && !currentFoot) {
      currentDrawer.appendChild(freshFoot.cloneNode(true));
    } else if (!freshFoot && currentFoot) {
      currentFoot.remove();
    }
  };

  const refreshCartAfterQuickViewAdd = async () => {
    const response = await fetch(`${ROOT_URL}cart.js`, {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) {
      throw new Error('The bag could not be refreshed.');
    }

    const cart = await response.json();
    updateCartBadges(Number(cart.item_count) || 0);

    try {
      await refreshCartDrawerMarkup();
    } catch (error) {
      console.warn('Quick View cart refresh warning:', error);
    }

    forceCartDrawerClosed();

    document.dispatchEvent(new CustomEvent('cart:updated', {
      bubbles: true,
      detail: { cart },
    }));

    return cart;
  };

  const parseCartError = async (response) => {
    try {
      const payload = await response.json();
      return payload?.description
        || payload?.message
        || 'This item could not be added to the cart.';
    } catch (error) {
      return 'This item could not be added. Please try again.';
    }
  };

  const ensureModal = () => {
    let modal = document.getElementById('vexx-quick-view');
    if (modal) return modal;

    modal = document.createElement('div');
    modal.id = 'vexx-quick-view';
    modal.className = 'quick-view';
    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    modal.innerHTML = `
      <button
        class="quick-view__overlay"
        type="button"
        aria-label="Close quick view"
        data-quick-view-close
      ></button>

      <section
        class="quick-view__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="quick-view-title"
        tabindex="-1"
      >
        <button
          class="quick-view__close"
          type="button"
          aria-label="Close quick view"
          data-quick-view-close
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M5 5L19 19M19 5L5 19"
              stroke="currentColor"
              stroke-width="2.2"
              stroke-linecap="round"
            ></path>
          </svg>
        </button>

        <div class="quick-view__content" data-quick-view-content>
          <div class="quick-view__loading" role="status">Loading product…</div>
        </div>
      </section>`;

    document.body.appendChild(modal);
    return modal;
  };

  const closeQuickView = () => {
    const modal = document.getElementById('vexx-quick-view');
    if (!modal || modal.hidden) return;

    window.clearTimeout(closeTimer);
    modal.classList.remove('open');
    modal.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('quick-view-is-open');
    document.body.classList.remove('quick-view-is-open');

    closeTimer = window.setTimeout(() => {
      modal.hidden = true;
      const content = modal.querySelector('[data-quick-view-content]');
      if (content) {
        content.innerHTML = '<div class="quick-view__loading" role="status">Loading product…</div>';
      }
    }, 220);

    if (lastTrigger instanceof HTMLElement && lastTrigger.isConnected) {
      lastTrigger.focus({ preventScroll: true });
    }
  };

  const renderProduct = (modal, product) => {
    const content = modal.querySelector('[data-quick-view-content]');
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const initialVariant = variants.find((variant) => variant.available) || variants[0];

    if (!content || !initialVariant) {
      throw new Error('No product variants were found.');
    }

    const options = productOptions(product);
    const initialOptions = variantOptions(initialVariant);
    const productUrl = `${ROOT_URL}products/${encodeURIComponent(product.handle)}`;
    const initialImage = initialVariant.featured_image?.src
      || product.featured_image
      || (Array.isArray(product.images) ? product.images[0] : '');

    const optionsHtml = options.map((option, optionIndex) => {
      const values = option.values.length
        ? option.values
        : [...new Set(variants.map((variant) => variantOptions(variant)[optionIndex]).filter(Boolean))];
      const selectedValue = initialOptions[optionIndex] || values[0] || '';

      return `
        <fieldset class="quick-view__option" data-quick-view-option-group="${optionIndex + 1}">
          <legend>
            <span>${escapeHtml(option.name)}</span>
            <strong data-quick-view-selected="${optionIndex + 1}">${escapeHtml(selectedValue)}</strong>
          </legend>

          <div class="quick-view__option-values">
            ${values.map((value) => {
              const active = String(value) === String(selectedValue);
              return `
                <button
                  class="quick-view__option-value${active ? ' active' : ''}"
                  type="button"
                  data-quick-view-option
                  data-option-position="${optionIndex + 1}"
                  data-option-value="${escapeHtml(value)}"
                  aria-pressed="${active ? 'true' : 'false'}"
                >${escapeHtml(value)}</button>`;
            }).join('')}
          </div>
        </fieldset>`;
    }).join('');

    content.innerHTML = `
      <div class="quick-view__media">
        ${initialImage
          ? `<img src="${escapeHtml(initialImage)}" alt="${escapeHtml(product.title)}" data-quick-view-image>`
          : '<div class="quick-view__image-placeholder">Product photo</div>'}
      </div>

      <div class="quick-view__details">
        <a class="quick-view__title-link" href="${escapeHtml(productUrl)}">
          <h2 id="quick-view-title" class="quick-view__title">${escapeHtml(product.title)}</h2>
        </a>

        <div class="quick-view__price-row">
          <span class="quick-view__price" data-quick-view-price>${formatMoney(initialVariant.price)}</span>
          <span class="quick-view__compare" data-quick-view-compare hidden></span>
        </div>

        <form class="quick-view__form" data-quick-view-form>
          <input type="hidden" name="id" value="${escapeHtml(initialVariant.id)}" data-quick-view-variant-id>
          ${optionsHtml}

          <div class="quick-view__purchase-row">
            <div class="quick-view__quantity" aria-label="Quantity selector">
              <button type="button" aria-label="Decrease quantity" data-quick-view-qty-minus>−</button>
              <input
                type="number"
                name="quantity"
                value="1"
                min="1"
                inputmode="numeric"
                aria-label="Quantity"
                data-quick-view-quantity
              >
              <button type="button" aria-label="Increase quantity" data-quick-view-qty-plus>+</button>
            </div>

            <button class="btn btn-primary btn-lg quick-view__add" type="submit" data-quick-view-add>
              ADD TO CART
            </button>
          </div>

          <p class="quick-view__availability" data-quick-view-availability></p>
          <p class="quick-view__error" data-quick-view-error role="alert" hidden></p>
        </form>

        <a class="quick-view__full-details" href="${escapeHtml(productUrl)}">VIEW FULL PRODUCT DETAILS</a>
      </div>`;

    const form = content.querySelector('[data-quick-view-form]');
    const variantIdInput = content.querySelector('[data-quick-view-variant-id]');
    const price = content.querySelector('[data-quick-view-price]');
    const compare = content.querySelector('[data-quick-view-compare]');
    const image = content.querySelector('[data-quick-view-image]');
    const addButton = content.querySelector('[data-quick-view-add]');
    const availability = content.querySelector('[data-quick-view-availability]');
    const errorMessage = content.querySelector('[data-quick-view-error]');
    const quantity = content.querySelector('[data-quick-view-quantity]');

    if (!form || !variantIdInput || !price || !compare || !addButton || !availability || !errorMessage || !quantity) {
      throw new Error('The quick-view form could not be initialized.');
    }

    const selectedOptions = () => options.map((option, index) => {
      const active = content.querySelector(
        `[data-quick-view-option][data-option-position="${index + 1}"].active`
      );
      return active?.dataset.optionValue || initialOptions[index] || option.values[0] || '';
    });

    const selectedVariant = () => {
      const selected = selectedOptions();
      return variants.find((variant) => {
        const values = variantOptions(variant);
        return selected.every((value, index) => String(values[index]) === String(value));
      });
    };

    const updateOptionAvailability = () => {
      const selected = selectedOptions();

      content.querySelectorAll('[data-quick-view-option]').forEach((button) => {
        const optionIndex = Number(button.dataset.optionPosition) - 1;
        const candidate = [...selected];
        candidate[optionIndex] = button.dataset.optionValue;

        const available = variants.some((variant) => {
          if (!variant.available) return false;
          const values = variantOptions(variant);
          return candidate.every((value, index) => String(values[index]) === String(value));
        });

        button.disabled = !available && !button.classList.contains('active');
      });
    };

    const updateVariant = () => {
      const variant = selectedVariant();
      errorMessage.hidden = true;
      errorMessage.textContent = '';

      if (!variant) {
        variantIdInput.value = '';
        addButton.disabled = true;
        addButton.textContent = 'UNAVAILABLE';
        availability.textContent = 'This combination is unavailable.';
        updateOptionAvailability();
        return;
      }

      variantIdInput.value = String(variant.id);
      price.textContent = formatMoney(variant.price);

      const compareAt = Number(variant.compare_at_price) || 0;
      const currentPrice = Number(variant.price) || 0;
      const isOnSale = compareAt > currentPrice;

      compare.hidden = !isOnSale;
      compare.textContent = isOnSale ? formatMoney(compareAt) : '';
      price.classList.toggle('quick-view__price--sale', isOnSale);

      addButton.disabled = !variant.available;
      addButton.textContent = variant.available ? 'ADD TO CART' : 'SOLD OUT';
      availability.textContent = variant.available
        ? 'Ready to add to your bag.'
        : 'This variant is sold out.';

      if (image && variant.featured_image?.src) {
        image.src = variant.featured_image.src;
      }

      updateOptionAvailability();
    };

    content.querySelectorAll('[data-quick-view-option]').forEach((button) => {
      button.addEventListener('click', () => {
        const position = button.dataset.optionPosition;

        content
          .querySelectorAll(`[data-quick-view-option][data-option-position="${position}"]`)
          .forEach((peer) => {
            const active = peer === button;
            peer.classList.toggle('active', active);
            peer.setAttribute('aria-pressed', active ? 'true' : 'false');
          });

        const selectedLabel = content.querySelector(`[data-quick-view-selected="${position}"]`);
        if (selectedLabel) selectedLabel.textContent = button.dataset.optionValue || '';

        updateVariant();
      });
    });

    content.querySelector('[data-quick-view-qty-minus]')?.addEventListener('click', () => {
      quantity.value = String(Math.max(1, Number(quantity.value || 1) - 1));
    });

    content.querySelector('[data-quick-view-qty-plus]')?.addEventListener('click', () => {
      quantity.value = String(Math.max(1, Number(quantity.value || 1) + 1));
    });

    quantity.addEventListener('change', () => {
      quantity.value = String(Math.max(1, Number(quantity.value) || 1));
    });

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      event.stopPropagation();

      if (!variantIdInput.value || addButton.disabled) return;
      if (form.dataset.quickViewAdding === 'true') return;

      form.dataset.quickViewAdding = 'true';

      const originalText = addButton.textContent.trim() || 'ADD TO CART';
      addButton.disabled = true;
      addButton.setAttribute('aria-busy', 'true');
      addButton.textContent = 'ADDING...';
      errorMessage.hidden = true;
      errorMessage.textContent = '';

      try {
        const response = await fetch(`${ROOT_URL}cart/add.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
          body: new FormData(form),
        });

        if (!response.ok) {
          throw new Error(await parseCartError(response));
        }

        await response.json();
        const cart = await refreshCartAfterQuickViewAdd();

        addButton.textContent = 'ADDED ✓';
        availability.textContent = 'Added successfully. You can continue shopping.';

        showCartToast({
          type: 'success',
          title: 'ADDED TO YOUR BAG',
          message: `${product.title} was added successfully. Bag total: ${cart.item_count} item${cart.item_count === 1 ? '' : 's'}.`,
        });

        forceCartDrawerClosed();

        window.setTimeout(() => {
          addButton.disabled = false;
          addButton.removeAttribute('aria-busy');
          addButton.textContent = originalText;
        }, 1400);
      } catch (error) {
        addButton.disabled = false;
        addButton.removeAttribute('aria-busy');
        addButton.textContent = originalText;
        errorMessage.textContent = error instanceof Error
          ? error.message
          : 'This item could not be added. Please try again.';
        errorMessage.hidden = false;

        showCartToast({
          type: 'error',
          title: 'COULD NOT ADD ITEM',
          message: errorMessage.textContent,
        });
      } finally {
        form.dataset.quickViewAdding = 'false';
      }
    });

    updateVariant();
  };

  const openQuickView = async (trigger) => {
    const handle = trigger?.dataset.productHandle;
    if (!handle) return;

    lastTrigger = trigger;
    window.clearTimeout(closeTimer);

    const modal = ensureModal();
    const content = modal.querySelector('[data-quick-view-content]');
    if (!content) return;

    content.innerHTML = '<div class="quick-view__loading" role="status">Loading product…</div>';
    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('quick-view-is-open');
    document.body.classList.add('quick-view-is-open');

    window.requestAnimationFrame(() => {
      modal.classList.add('open');
      modal.querySelector('.quick-view__dialog')?.focus({ preventScroll: true });
    });

    try {
      const response = await fetch(`${ROOT_URL}products/${encodeURIComponent(handle)}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });

      if (!response.ok) {
        throw new Error('Product details could not be loaded.');
      }

      renderProduct(modal, await response.json());
    } catch (error) {
      content.innerHTML = `
        <div class="quick-view__load-error">
          <p>${escapeHtml(error.message || 'Product details could not be loaded.')}</p>
          <a class="btn btn-dark btn-md" href="${escapeHtml(trigger.closest('.product-card')?.href || '#')}">
            VIEW PRODUCT
          </a>
        </div>`;
    }
  };

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const trigger = target.closest('[data-product-quick-view]');
    if (trigger) {
      event.preventDefault();
      event.stopPropagation();
      openQuickView(trigger);
      return;
    }

    if (target.closest('[data-quick-view-close]')) {
      event.preventDefault();
      closeQuickView();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closeQuickView();
      return;
    }

    if (event.key !== 'Enter' && event.key !== ' ') return;

    const target = event.target instanceof Element
      ? event.target.closest('[data-product-quick-view]')
      : null;

    if (!target) return;

    event.preventDefault();
    target.click();
  });
})();
